/* ==========================================================================
   WAVELESS — global.js
   Cart drawer (AJAX + Section Rendering API), product forms, variant
   selection, quick view, predictive search, menu drawer, localization.
   No external dependencies.
   ========================================================================== */
(function () {
  'use strict';

  /* ─── Utilities ─────────────────────────────────────────── */

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // Money formatting for client-side computed values (bundle totals).
  // All primary prices are server-rendered via money filters / the Section
  // Rendering API. For computed sums we use Intl with the cart's active
  // presentment currency (Shopify Markets), falling back to the shop's
  // money format string.
  window.formatMoney = function (cents, format) {
    if (!format && window.theme && window.theme.cartCurrency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: window.theme.cartCurrency
        }).format(cents / 100);
      } catch (e) { /* fall through to format-string path */ }
    }
    return formatMoneyWithFormat(cents, format);
  };

  function formatMoneyWithFormat(cents, format) {
    format = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function delimit(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? dollars + decimal + parts[1] : dollars;
    }

    let value = '';
    switch (format.match(placeholderRegex)[1]) {
      case 'amount': value = delimit(cents, 2); break;
      case 'amount_no_decimals': value = delimit(cents, 0); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
      default: value = delimit(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  };

  /* ─── Toast ─────────────────────────────────────────────── */

  let toastTimer;
  window.showToast = function (msg) {
    const toast = document.getElementById('Toast');
    if (!toast) return;
    toast.querySelector('.toast-msg').textContent = msg;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2600);
  };

  /* ─── Focus trap (drawers & modals, accessibility) ──────── */

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let releaseTrap = null;

  function trapFocus(container, initial) {
    releaseFocus();
    const handler = function (e) {
      if (e.key !== 'Tab') return;
      const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    releaseTrap = () => document.removeEventListener('keydown', handler);
    (initial || container.querySelector(FOCUSABLE) || container).focus({ preventScroll: true });
  }

  function releaseFocus() {
    if (releaseTrap) { releaseTrap(); releaseTrap = null; }
  }

  /* ─── Scrim (shared overlay) ────────────────────────────── */

  function getScrim() {
    let scrim = document.getElementById('Scrim');
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.id = 'Scrim';
      scrim.className = 'scrim';
      document.body.appendChild(scrim);
      scrim.addEventListener('click', closeAllOverlays);
    }
    return scrim;
  }

  function closeAllOverlays() {
    document.querySelectorAll('.drawer.open, .menu-drawer.open, .search-modal.open, .facets.open').forEach((el) => el.classList.remove('open'));
    document.querySelectorAll('.qv-overlay.open').forEach((el) => el.remove());
    getScrim().classList.remove('open');
    document.body.style.overflow = '';
    releaseFocus();
  }
  window.closeAllOverlays = closeAllOverlays;

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllOverlays();
      document.querySelectorAll('.lightbox.open, .wl-modal.open').forEach((el) => el.classList.remove('open'));
      document.querySelectorAll('details.loc[open]').forEach((d) => d.removeAttribute('open'));
    }
  });

  function openOverlay(el, initialFocus) {
    document.querySelectorAll('.drawer.open, .menu-drawer.open, .search-modal.open, .facets.open').forEach((other) => {
      if (other !== el) other.classList.remove('open');
    });
    el.classList.add('open');
    getScrim().classList.add('open');
    document.body.style.overflow = 'hidden';
    trapFocus(el, initialFocus);
  }
  window.openOverlay = openOverlay;

  /* ─── Header: scroll state ──────────────────────────────── */

  const nav = document.querySelector('.nav');
  if (nav) {
    const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─── Close <details> popovers on outside click ─────────── */

  document.addEventListener('click', (e) => {
    document.querySelectorAll('details.loc[open]').forEach((details) => {
      if (!details.contains(e.target)) details.removeAttribute('open');
    });
  });

  /* ─── Cart: shared helpers ──────────────────────────────── */

  const CART_SECTIONS = ['cart-drawer'];

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = '[' + count + ']'; });
  }

  function renderCartSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const doc = parseHTML(sections['cart-drawer']);
      const fresh = doc.querySelector('#CartDrawer');
      const current = document.querySelector('#CartDrawer');
      if (fresh && current) {
        const wasOpen = current.classList.contains('open');
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) current.classList.add('open');
        const countEl = current.querySelector('[data-drawer-count]');
        if (countEl) updateCartCount(countEl.textContent.trim());
      }
    }
    // Cart page section (id passed dynamically)
    Object.keys(sections).forEach((key) => {
      const target = document.querySelector('[data-cart-section][data-section-id="' + key + '"]');
      if (target) {
        const doc = parseHTML(sections[key]);
        const fresh = doc.querySelector('[data-cart-section]');
        if (fresh) target.innerHTML = fresh.innerHTML;
      }
    });
  }

  function cartSectionsToRequest() {
    const list = CART_SECTIONS.slice();
    const cartPage = document.querySelector('[data-cart-section]');
    if (cartPage && cartPage.dataset.sectionId) list.push(cartPage.dataset.sectionId);
    return list;
  }

  window.openCartDrawer = function () {
    const drawer = document.querySelector('#CartDrawer');
    if (drawer) openOverlay(drawer, drawer.querySelector('.drawer-head .close'));
  };

  async function refreshCart() {
    try {
      const res = await fetch(window.theme.routes.cart + '?sections=' + cartSectionsToRequest().join(','));
      const sections = await res.json();
      renderCartSections(sections);
      const cartRes = await fetch(window.theme.routes.cart + '.js');
      const cart = await cartRes.json();
      updateCartCount(cart.item_count);
    } catch (e) { /* network hiccup: leave current UI */ }
  }
  window.refreshCart = refreshCart;

  async function changeCartLine(key, quantity) {
    const body = { id: key, quantity: quantity, sections: cartSectionsToRequest() };
    const res = await fetch(window.theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (data.errors || data.status === 422) {
      window.showToast(typeof data.errors === 'string' ? data.errors : window.theme.strings.quantityError);
      await refreshCart();
      return;
    }
    renderCartSections(data.sections);
    updateCartCount(data.item_count);
  }

  // Event delegation: quantity steppers + remove, works in drawer and cart page.
  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-line-key]');
      const current = parseInt(line.dataset.lineQty, 10);
      const next = qtyBtn.dataset.cartQty === 'inc' ? current + 1 : current - 1;
      changeCartLine(line.dataset.lineKey, Math.max(0, next));
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const line = removeBtn.closest('[data-line-key]');
      changeCartLine(line.dataset.lineKey, 0);
    }
  });

  document.addEventListener('change', (e) => {
    const qtyInput = e.target.closest('input[data-cart-qty-input]');
    if (qtyInput) {
      const line = qtyInput.closest('[data-line-key]');
      changeCartLine(line.dataset.lineKey, Math.max(0, parseInt(qtyInput.value, 10) || 0));
    }
  });

  // Open-cart triggers (bag button)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-cart]');
    if (trigger && document.querySelector('#CartDrawer')) {
      e.preventDefault();
      window.openCartDrawer();
    }
    const closeTrigger = e.target.closest('[data-close-overlay]');
    if (closeTrigger) { e.preventDefault(); closeAllOverlays(); }
  });

  /* ─── Product form (AJAX add to cart) ───────────────────── */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      e.preventDefault();
      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;
      if (submitBtn) submitBtn.classList.add('loading');

      const formData = new FormData(this.form);
      formData.append('sections', cartSectionsToRequest().join(','));
      formData.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(window.theme.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const data = await res.json();
        if (data.status && data.status !== 200) {
          window.showToast(data.description || window.theme.strings.cartError);
          return;
        }
        renderCartSections(data.sections);
        const cartRes = await fetch(window.theme.routes.cart + '.js');
        const cart = await cartRes.json();
        updateCartCount(cart.item_count);
        window.showToast(window.theme.strings.addedToBag);
        const qv = this.closest('.qv-overlay');
        if (qv) qv.remove();
        if (document.querySelector('#CartDrawer')) {
          setTimeout(() => window.openCartDrawer(), 350);
        }
      } catch (err) {
        window.showToast(window.theme.strings.cartError);
      } finally {
        if (submitBtn) submitBtn.classList.remove('loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ─── Variant selection ─────────────────────────────────── */
  /*
   * <variant-selects> wraps option inputs (radios or selects).
   * On change it resolves the matching variant from embedded JSON, then:
   *  - updates the hidden [name="id"] input + button state instantly
   *  - swaps price / SKU / availability via the Section Rendering API,
   *    so prices stay correct for every Shopify Markets currency
   *  - switches the gallery to the variant's featured media
   *  - updates the URL (?variant=) on product pages
   */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-info]') || document;
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl === 'true';
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      } catch (e) { this.variants = []; }
      this.addEventListener('change', this.onChange.bind(this));
      this.markAvailability();

      // Keep selection in sync with the ?variant= URL parameter when the
      // user navigates browser history (back/forward, bfcache restores).
      if (this.updateUrl) {
        window.addEventListener('popstate', () => {
          const vid = new URLSearchParams(window.location.search).get('variant');
          if (!vid) return;
          const variant = this.variants.find((v) => String(v.id) === vid);
          if (!variant || variant === this.currentVariant) return;
          variant.options.forEach((val, i) => {
            this.querySelectorAll('fieldset[data-option-index="' + i + '"] input').forEach((input) => {
              input.checked = input.value === val;
            });
            const sel = this.querySelector('select[data-option-index="' + i + '"]');
            if (sel) sel.value = val;
          });
          this.suppressUrl = true;
          this.onChange();
          this.suppressUrl = false;
        });
      }
    }

    selectedOptions() {
      const opts = [];
      this.querySelectorAll('fieldset[data-option-index]').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        opts[parseInt(fs.dataset.optionIndex, 10)] = checked ? checked.value : null;
      });
      this.querySelectorAll('select[data-option-index]').forEach((sel) => {
        opts[parseInt(sel.dataset.optionIndex, 10)] = sel.value;
      });
      return opts;
    }

    findVariant(options) {
      return this.variants.find((v) => options.every((val, i) => v.options[i] === val));
    }

    onChange() {
      // reflect radio selection visually
      this.querySelectorAll('fieldset[data-option-index] label').forEach((l) => {
        const input = l.querySelector('input');
        if (input) l.classList.toggle('sel', input.checked);
      });
      this.querySelectorAll('[data-option-value-label]').forEach((el) => {
        const idx = parseInt(el.dataset.optionValueLabel, 10);
        const val = this.selectedOptions()[idx];
        if (val) el.textContent = val;
      });

      const variant = this.findVariant(this.selectedOptions());
      this.currentVariant = variant;
      this.markAvailability();
      this.updateButton(variant);
      this.updateMedia(variant);
      this.updateHiddenInput(variant);
      if (variant && this.updateUrl && !this.suppressUrl) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }
      this.renderFromSection(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
    }

    updateHiddenInput(variant) {
      const input = this.root.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      if (input && variant) input.value = variant.id;
    }

    updateButton(variant) {
      const btn = this.root.querySelector('form[data-type="add-to-cart-form"] [type="submit"]');
      if (!btn) return;
      const label = btn.querySelector('[data-atc-label]') || btn;
      if (!variant) {
        btn.setAttribute('aria-disabled', 'true');
        label.textContent = window.theme.strings.unavailable;
      } else if (!variant.available) {
        btn.setAttribute('aria-disabled', 'true');
        label.textContent = window.theme.strings.soldOut;
      } else {
        btn.removeAttribute('aria-disabled');
        label.textContent = window.theme.strings.addToCart;
      }
    }

    updateMedia(variant) {
      const gallery = this.root.querySelector('[data-gallery]');
      if (!gallery) return;
      // Filter the gallery down to the selected color's media first
      // (variant featured images + alt-text convention, see product.js).
      const colorIdx = parseInt(this.dataset.colorIndex != null ? this.dataset.colorIndex : '-1', 10);
      if (typeof gallery.filterMedia === 'function' && colorIdx > -1 && variant) {
        gallery.filterMedia((variant.options[colorIdx] || '').toLowerCase().trim());
      }
      if (!variant || !variant.featured_media) return;
      const id = String(variant.featured_media.id);
      if (typeof gallery.setActiveMedia === 'function') {
        // product.js slider API: also swipes to the media on mobile
        gallery.setActiveMedia(id, true);
        return;
      }
      const target = gallery.querySelector('[data-media-id="' + id + '"]');
      if (target) {
        gallery.querySelectorAll('.media-item').forEach((m) => m.classList.remove('active'));
        target.classList.add('active');
        gallery.querySelectorAll('.pdp-thumb').forEach((t) => {
          t.classList.toggle('sel', t.dataset.mediaTarget === id);
        });
      }
    }

    // Server-render price/SKU/availability for currency correctness.
    async renderFromSection(variant) {
      if (!variant || !this.sectionId) return;
      try {
        const res = await fetch(this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId);
        const html = await res.text();
        const doc = parseHTML(html);
        ['[data-price-wrap]', '[data-sku-wrap]', '[data-inventory-wrap]', '[data-sticky-price]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = this.root.querySelector(sel) || document.querySelector(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      } catch (e) { /* keep optimistic UI */ }
    }

    // Dim/strike option values that lead to no available variant.
    markAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('fieldset[data-option-index]').forEach((fs) => {
        const idx = parseInt(fs.dataset.optionIndex, 10);
        fs.querySelectorAll('label[data-option-value]').forEach((labelEl) => {
          const value = labelEl.dataset.optionValue;
          const test = selected.slice();
          test[idx] = value;
          const match = this.variants.find((v) => test.every((val, i) => val == null || v.options[i] === val));
          const availableMatch = this.variants.find((v) => v.available && test.every((val, i) => val == null || v.options[i] === val));
          labelEl.classList.toggle('unavailable', !availableMatch);
          labelEl.title = !availableMatch ? window.theme.strings.soldOut : value;
          if (!match) labelEl.classList.add('unavailable');
        });
      });
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* ─── Quantity input ────────────────────────────────────── */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.qty-input [data-qty]');
    if (!btn) return;
    const input = btn.closest('.qty-input').querySelector('input');
    const step = btn.dataset.qty === 'inc' ? 1 : -1;
    input.value = Math.max(parseInt(input.min, 10) || 1, (parseInt(input.value, 10) || 1) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ─── Quick view ────────────────────────────────────────── */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const url = btn.dataset.quickView;
    const sep = url.includes('?') ? '&' : '?';
    btn.classList.add('loading');
    try {
      const res = await fetch(url + sep + 'section_id=quick-view');
      const html = await res.text();
      const doc = parseHTML(html);
      const modal = doc.querySelector('.qv-overlay');
      if (!modal) return;
      const root = document.getElementById('QuickViewRoot');
      root.innerHTML = '';
      root.appendChild(modal);
      requestAnimationFrame(() => {
        modal.classList.add('open');
        trapFocus(modal, modal.querySelector('.qv-close'));
      });
      modal.addEventListener('click', (ev) => {
        if (ev.target === modal || ev.target.closest('.qv-close')) {
          modal.remove();
          releaseFocus();
        }
      });
    } finally {
      btn.classList.remove('loading');
    }
  });

  /* ─── Menu drawer / search modal triggers ───────────────── */

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-menu]')) {
      e.preventDefault();
      const drawer = document.getElementById('MenuDrawer');
      if (drawer) openOverlay(drawer);
    }
    if (e.target.closest('[data-open-search]')) {
      e.preventDefault();
      const modal = document.getElementById('SearchModal');
      if (modal) openOverlay(modal, modal.querySelector('input[type="search"]'));
    }
    if (e.target.closest('[data-open-filters]')) {
      e.preventDefault();
      const facets = document.querySelector('.facets');
      if (facets) openOverlay(facets);
    }
  });

  /* ─── Predictive search ─────────────────────────────────── */

  const searchInput = document.querySelector('#SearchModal input[type="search"]');
  if (searchInput) {
    const resultsEl = document.querySelector('#PredictiveResults');
    searchInput.addEventListener('input', debounce(async () => {
      const q = searchInput.value.trim();
      if (q.length < 2) { resultsEl.innerHTML = ''; return; }
      try {
        const url = window.theme.routes.predictiveSearch +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,query,collection&resources[limit]=6&section_id=predictive-search';
        const res = await fetch(url);
        const html = await res.text();
        const doc = parseHTML(html);
        const fresh = doc.querySelector('#PredictiveResultsContent');
        resultsEl.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (e) { /* ignore */ }
    }, 250));
  }

  /* ─── Localization forms: auto-submit on change ─────────── */

  document.addEventListener('change', (e) => {
    if (e.target.matches('select[name="country_code"], select[name="locale_code"]')) {
      const form = e.target.closest('form');
      if (form && form.action && form.action.includes('/localization')) form.submit();
    }
  });

  /* ─── Cart drawer upsell add ────────────────────────────── */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      const res = await fetch(window.theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.upsellAdd, 10), quantity: 1 }], sections: cartSectionsToRequest() })
      });
      const data = await res.json();
      if (data.status && data.status !== 200) {
        window.showToast(data.description || window.theme.strings.cartError);
        return;
      }
      renderCartSections(data.sections);
      const cartRes = await fetch(window.theme.routes.cart + '.js');
      const cart = await cartRes.json();
      updateCartCount(cart.item_count);
      window.showToast(window.theme.strings.addedToBag);
    } finally {
      btn.disabled = false;
    }
  });

  /* ─── Related products slider (arrows scroll the track) ──── */

  function initRelatedSlider(section) {
    const track = section.querySelector('[data-related-track]');
    const prev = section.querySelector('[data-related-prev]');
    const next = section.querySelector('[data-related-next]');
    if (!track || track.dataset.relatedInit === '1') return;
    track.dataset.relatedInit = '1';

    function page() {
      // one column width incl. gap
      const first = track.querySelector('.product-card');
      const gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
      const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
      const cols = parseInt(getComputedStyle(track).getPropertyValue('--related-cols'), 10) || 1;
      return step * Math.max(1, cols - 1);
    }
    function updateDisabled() {
      if (!prev || !next) return;
      prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      next.toggleAttribute('disabled', track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
    }
    if (prev) prev.addEventListener('click', () => { track.scrollBy({ left: -page(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', () => { track.scrollBy({ left: page(), behavior: 'smooth' }); });
    track.addEventListener('scroll', updateDisabled, { passive: true });
    updateDisabled();
  }

  window.initRelatedSliders = function (root) {
    (root || document).querySelectorAll('.related-products').forEach(initRelatedSlider);
  };
  window.initRelatedSliders(document);
  document.addEventListener('shopify:section:load', (e) => window.initRelatedSliders(e.target));

  /* ─── Autoplay-ensure for cover videos (editor/browser-safe) ──
     Muted autoplay is occasionally not started by the browser/theme
     editor even with the attributes present. Nudge it, idempotently. */
  function ensureVideoAutoplay(root) {
    (root || document).querySelectorAll('video.wl-video[autoplay]').forEach((v) => {
      if (v.dataset.autoplayInit === '1') return;
      v.dataset.autoplayInit = '1';
      v.muted = true; // required for programmatic play()
      const tryPlay = () => { const p = v.play(); if (p && p.catch) p.catch(function () {}); };
      if (v.readyState >= 2) tryPlay();
      v.addEventListener('canplay', tryPlay, { once: true });
    });
  }
  ensureVideoAutoplay(document);
  document.addEventListener('shopify:section:load', (e) => ensureVideoAutoplay(e.target));
})();
